import { createServerFn } from "@tanstack/react-start";
import { trafficFieldsFromHeaders, trafficTermContentFromHeaders } from "@/lib/traffic-fields";
import { getRequestHeader } from "@tanstack/react-start/server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { grantsAssessmentSchema } from "@/lib/grants-assessment-schema";

const RETROFIT_BUCKET = "retrofit-assessments";
const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24 * 30; // 30 days

async function fireZapierWebhook(payload: Record<string, unknown>) {
  const url = process.env.ZAPIER_ENQUIRY_WEBHOOK_URL;
  if (!url) {
    console.warn("ZAPIER_ENQUIRY_WEBHOOK_URL is not configured; skipping webhook");
    return;
  }
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      console.error(`Zapier webhook returned ${res.status}: ${text}`);
    }
  } catch (err) {
    console.error("Zapier webhook fetch failed:", err);
  }
}

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 120);
}

async function uploadAssessmentFile(
  attachment: NonNullable<ReturnType<typeof grantsAssessmentSchema.parse>["attachment"]>
): Promise<
  { storagePath: string; signedUrl: string; fileName: string } | { error: string }
> {
  const buffer = Buffer.from(attachment.base64, "base64");
  if (buffer.length > 10 * 1024 * 1024) {
    return { error: "File must be 10MB or smaller." };
  }

  const storagePath = `${Date.now()}-${sanitizeFileName(attachment.fileName)}`;
  const { error: uploadError } = await supabaseAdmin.storage
    .from(RETROFIT_BUCKET)
    .upload(storagePath, buffer, {
      contentType: attachment.contentType,
      upsert: false,
    });

  if (uploadError) {
    console.error("Retrofit assessment upload failed:", uploadError);
    return { error: "We couldn't upload your file. Please try again or email it to us." };
  }

  const { data: signed, error: signError } = await supabaseAdmin.storage
    .from(RETROFIT_BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);

  if (signError || !signed?.signedUrl) {
    console.error("Signed URL creation failed:", signError);
    return {
      storagePath,
      signedUrl: `(storage:${RETROFIT_BUCKET}/${storagePath})`,
      fileName: attachment.fileName,
    };
  }

  return { storagePath, signedUrl: signed.signedUrl, fileName: attachment.fileName };
}

export const submitGrantsAssessment = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => grantsAssessmentSchema.parse(input))
  .handler(async ({ data }) => {
    if (data.honeypot && data.honeypot.length > 0) {
      return { success: true as const, id: `honeypot-${crypto.randomUUID()}` };
    }

    let attachmentUrl: string | null = null;
    let attachmentStoragePath: string | null = null;
    let attachmentFileName: string | null = null;

    if (data.attachment) {
      const uploaded = await uploadAssessmentFile(data.attachment);
    if ("error" in uploaded) {
      return { success: false as const, error: uploaded.error };
    }
      attachmentUrl = uploaded.signedUrl;
      attachmentStoragePath = uploaded.storagePath;
      attachmentFileName = uploaded.fileName;
    }

    const userAgent = getRequestHeader("user-agent")?.slice(0, 1000) ?? null;

    const messageParts = [
      data.property_type ? `Property type: ${data.property_type}` : null,
      data.message?.trim() ? data.message.trim() : null,
      attachmentStoragePath
        ? `Assessment file: ${RETROFIT_BUCKET}/${attachmentStoragePath}`
        : null,
    ].filter(Boolean);

    const id = crypto.randomUUID();

    const insertRow = {
      id,
      form_type: "grants_assessment",
      name: data.name.trim(),
      phone: data.phone.trim(),
      email: data.email?.trim() || null,
      postcode: data.postcode.trim(),
      service_of_interest: data.property_type?.trim() || "Retrofit assessment install",
      message: messageParts.length > 0 ? messageParts.join("\n\n") : null,
      source_page: data.source_page,
      gclid: data.gclid?.trim() || null,
      user_agent: userAgent,
      attachment_url: attachmentUrl,
      ...trafficFieldsFromHeaders(getRequestHeader),
    };

    const { error } = await supabaseAdmin.from("form_submissions").insert(insertRow);

    if (error) {
      console.error("Failed to insert grants assessment submission:", error);
      return {
        success: false as const,
        error: "We couldn't send your assessment. Please try again or call us on 0800 009 2992.",
      };
    }

    const submittedAt = new Date().toISOString();
    const subject = `Retrofit assessment received: ${insertRow.name} (${insertRow.postcode})`;

    await fireZapierWebhook({
      subject,
      lead_id: id,
      submitted_at: submittedAt,
      form_type: insertRow.form_type,
      name: insertRow.name,
      phone: insertRow.phone,
      email: insertRow.email,
      postcode: insertRow.postcode,
      property_type: data.property_type?.trim() || null,
      service_of_interest: insertRow.service_of_interest,
      message: insertRow.message,
      source_page: insertRow.source_page,
      attachment_url: attachmentUrl,
      attachment_filename: attachmentFileName,
      attachment_storage_path: attachmentStoragePath
        ? `${RETROFIT_BUCKET}/${attachmentStoragePath}`
        : null,
      uploads_dashboard_url: "https://crsenergy.org/uploads",
      gclid: insertRow.gclid,
      user_agent: insertRow.user_agent,
      source: insertRow.traffic_source,
      source_medium: insertRow.traffic_medium,
      source_campaign: insertRow.traffic_campaign,
      source_referrer: insertRow.traffic_referrer,
      landing_page: insertRow.landing_page,
      ...trafficTermContentFromHeaders(getRequestHeader),
    });

    return { success: true as const, id };
  });
